// src/components/blog/BlogIndexClient.tsx
//
// Client half of the blog index: owns the category filter state and renders
// the post grid. The server wrapper loads posts once and passes them down.

'use client'

import { useMemo, useState } from 'react'
import type { BlogCategory, BlogPostMeta } from '@/lib/content/blog-types'
import ArticleCard from '@/components/blog/ArticleCard'
import CategoryFilter from '@/components/blog/CategoryFilter'

export interface BlogIndexClientProps {
  posts: BlogPostMeta[]
}

export default function BlogIndexClient({ posts }: BlogIndexClientProps) {
  const [active, setActive] = useState<BlogCategory | null>(null)

  const filtered = useMemo(() => {
    if (active === null) return posts
    return posts.filter((p) => p.category === active)
  }, [posts, active])

  // Featured post is the most recent overall (not category-filtered, so the
  // hero stays stable even when filters change).
  const featured = posts[0]
  const rest = active === null ? filtered.slice(1) : filtered

  return (
    <div className="space-y-10">
      {active === null && featured ? (
        <section aria-label="Featured article">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink">
            Latest
          </p>
          <div className="grid grid-cols-1 gap-6">
            <ArticleCard post={featured} featured />
          </div>
        </section>
      ) : null}

      <section aria-label="All articles" className="space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {active === null ? 'Browse all articles' : `${active} articles`}
          </h2>
          <CategoryFilter active={active} onChange={setActive} />
        </div>
        {rest.length === 0 ? (
          <p className="rounded-xl border border-border bg-card/40 p-8 text-center text-muted-foreground">
            No articles in this category yet. Check back soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((post) => (
              <ArticleCard key={post.slug} post={post} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
